import { CohortType } from '@dynamika-shared/types/cohorts';
import { ExtendedVisit } from '@components/SiteUserUpload/types/uploadTypes';
import { Visits } from '@dynamika-shared/types/subjects';

import { d7Format, parseDateOnly } from '@dynamika-shared/utils/date';

export type UnscheduledVisitName = {
  date: string;
  occurrence: number;
};

export const parseUnscheduledVisitName = (
  visitName?: string | null
): UnscheduledVisitName | null => {
  const UNSCHEDULED_VISIT_NAME = /^(\d{4}-\d{2}-\d{2})(?:-(\d+))?$/;

  const match = visitName?.trim().match(UNSCHEDULED_VISIT_NAME);

  if (!match) {
    return null;
  }

  const [, date, occurrence] = match;

  // The regex checks digit shape, not calendar validity — `2026-02-30` passes it.
  if (!parseDateOnly(date)) {
    return null;
  }

  return { date, occurrence: occurrence ? Number(occurrence) : 0 };
};

export const isUnscheduledVisitName = (visitName?: string | null) =>
  parseUnscheduledVisitName(visitName) !== null;

export const formatUnscheduledVisitName = (visitName?: string | null) => {
  const parsed = parseUnscheduledVisitName(visitName);

  if (!parsed) {
    return visitName ?? '';
  }

  const date = d7Format(parsed.date, 'dd-MMM-yyy');

  return parsed.occurrence ? `${date} (${parsed.occurrence})` : date;
};

export const getSortedVisits = (
  matchedCohort: CohortType | undefined,
  visits: Visits[] | undefined
): ExtendedVisit[] | undefined => {
  if (!matchedCohort || !visits) return undefined;

  return visits
    .map(visit => {
      const matchedVisit = matchedCohort.visits.find(
        matchedVisit => matchedVisit.id === visit.visitDefinitionId
      );

      if (!matchedVisit) {
        return undefined;
      }

      const extendedVisit: ExtendedVisit = {
        ...visit,
        value: matchedVisit.name,
        daysFromBaseline: matchedVisit.daysFromBaseline,
      };

      return extendedVisit;
    })
    .filter(visit => visit !== undefined) as ExtendedVisit[];
};

export const filterAndSortVisits = (
  visits: ExtendedVisit[],
  status: string
): ExtendedVisit[] => {
  const filteredVisits = visits.filter(visit => visit.status === status);
  [...filteredVisits].sort((a, b) => {
    // If both visits have the same status, compare their baselines
    if (a.status === b.status) {
      // Sort by baseline days in descending order
      return (a.daysFromBaseline ?? 0) - (b.daysFromBaseline ?? 0);
    }
    // Otherwise, prioritize IN_PROGRESS over CREATED
    if (a.status === 'IN_PROGRESS') {
      return -1; // a should come before b
    } else if (b.status === 'IN_PROGRESS') {
      return 1; // b should come before a
    }
    return 0; // maintain the order
  });
  return filteredVisits;
};

export const getFirstVisit = (
  visits: ExtendedVisit[]
): ExtendedVisit | undefined => {
  let minVisit: ExtendedVisit | undefined = undefined;

  for (const currentVisit of visits) {
    if (
      !minVisit ||
      (currentVisit.daysFromBaseline !== undefined &&
        currentVisit.daysFromBaseline < minVisit.daysFromBaseline)
    ) {
      minVisit = currentVisit;
    }
  }

  return minVisit;
};
